import { localPoint } from '@visx/event';
import { useTooltip } from '@visx/tooltip';
import { MouseEvent, ReactNode } from 'react';
import { PlotArea } from './plotArea';

/** The cursor over a chart's plot area, in pixels from the SVG's origin. */
export interface HoverPosition {
    x: number;
    y: number;
}

/**
 * A transparent rect over a chart's plot area that takes the mouse and
 * renders `children` with the cursor's position, or with undefined while
 * the cursor is outside the plot. The hover state lives here, so a move
 * re-renders only what `children` draws; the rect sits under them and the
 * overlays must take no pointer events of their own.
 */
export default function HoverSurface({
    plot,
    testId,
    children,
}: {
    plot: PlotArea;
    testId: string;
    children: (cursor: HoverPosition | undefined) => ReactNode;
}) {
    const { tooltipOpen, tooltipLeft, tooltipTop, showTooltip, hideTooltip } =
        useTooltip<undefined>();
    const onMouseMove = (event: MouseEvent<SVGRectElement>) => {
        const point = localPoint(event);
        if (point) {
            showTooltip({ tooltipLeft: point.x, tooltipTop: point.y });
        } else {
            hideTooltip();
        }
    };
    const cursor =
        tooltipOpen && tooltipLeft !== undefined && tooltipTop !== undefined
            ? { x: tooltipLeft, y: tooltipTop }
            : undefined;
    return (
        <>
            <rect
                data-testid={testId}
                x={plot.left}
                y={plot.top}
                width={plot.width}
                height={plot.height}
                fill="transparent"
                onMouseMove={onMouseMove}
                onMouseLeave={hideTooltip}
            />
            {children(cursor)}
        </>
    );
}
